import React from 'react';

import { UseFormReturn } from 'react-hook-form';

import { AccountCreateDTO } from '_lib/api/apiTypes.shim';
import { CheckboxFormItem } from '_lib/components/CheckboxFormItem/CheckboxFormItem';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { Stack } from '_lib/components/Stack/Stack';
import { TextFieldFormItem } from '_lib/components/TextFieldFormItem/TextFieldFormItem';
import { useMediaQuery } from '_lib/hooks/useMediaQuery';

export type AccountFormValues = Pick<AccountCreateDTO, 'name' | 'isCallTakingEnabled' | 'isPortalEnabled'>;

/**
 * Form is only responsible for fields it controls, and we can check for the generic constraint in the
 * props in order for this to be reusable.
 *
 * See https://github.com/orgs/react-hook-form/discussions/8311#discussioncomment-9759698
 */
type AccountFormProps<T extends AccountFormValues> = {
  accountFormContext: T extends AccountFormValues ? UseFormReturn<T> : never;
  isInactive?: boolean;
};

export const AccountForm = <T extends AccountFormValues>(props: AccountFormProps<T>) => {
  const { accountFormContext, isInactive } = props;

  const singleColumn = useMediaQuery((theme) => theme.breakpoints.only('xs'));

  const { control } = accountFormContext;

  return (
    <FlexBox width='100%'>
      <Stack direction={singleColumn ? 'column' : 'row'} spacing={2} useFlexGap width='100%'>
        <FlexBox sx={{ width: singleColumn ? '100%' : '33%' }}>
          <TextFieldFormItem name='name' label='Name' control={control} required disabled={isInactive} fullWidth />
        </FlexBox>
        <CheckboxFormItem
          name='isCallTakingEnabled'
          label='Call Taking Enabled'
          control={control}
          disabled={isInactive}
          sx={{ minWidth: 'fit-content' }}
        />
        <CheckboxFormItem
          name='isPortalEnabled'
          label='Portal Enabled'
          control={control}
          disabled={isInactive}
          sx={{ minWidth: 'fit-content' }}
        />
      </Stack>
    </FlexBox>
  );
};
